import store from '@/store'
import Utils from './Utils'
import axios, { AxiosError, isAxiosError } from 'axios';
import AuthManager from './AuthManager'

/**
 * Shared Axios wrapper for authenticated backend calls.
 *
 * On HTTP errors the server's `{ message }` is surfaced in a toast (and
 * re-thrown) so callers can show the same text inline. Only 401 forces a
 * session teardown — 403 (Forbidden / password-change required) keeps the
 * session so the user can see the real reason.
 */
export default class AxiosManager {

  baseUrl = ''
  accessToken = ''
  axiosConfig = {}

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
    this.accessToken = store.getters.getAccessToken
    this.axiosConfig = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + this.accessToken,
      }
    }
  }


  /**
   * Send a GET request to the server
   *
   * @param url The endpoint
   * @param id Selection id
   */
  public async get(url: string, id: number | null = null): Promise<any> {
    url = this.baseUrl + url
    if (id !== null)
      if (url.indexOf('?') !== -1)
        url = url + `&id=${id}`
      else
        `?id=${id}`

    // Inject pid
    const pid = Utils.getPid()
    if (url.indexOf('?') !== -1)
      url = url + `&pid=${pid}`
    else
      `?pid=${pid}`

    // Inject app mode
    const appMode = Utils.getAppMode().toLocaleLowerCase()
    if (url.indexOf('?') !== -1)
      url = url + `&app_mode=${appMode}`
    else
      `?app_mode=${appMode}`

    try {
      let res = await axios.get(url, this.axiosConfig)
      return res.data.payload
    } catch (error) {
      throw this.handleAxiosError(error)
    }
  }


  /**
   * Send a POST request to the server
   *
   * @param url The endpoint
   * @param payload POST payload
   */
  public async post(url: string, payload: any): Promise<any> {
    url = this.baseUrl + url

    // Inject pid and app mode
    const pid = Utils.getPid()
    const appMode = Utils.getAppMode().toLocaleLowerCase()
    payload = {
      ...payload,
      pid: pid,
      app_mode: appMode,
    }

    try {
      const res = await axios.post(url, payload, this.axiosConfig)
      return res.data.payload
    } catch (error) {
      throw this.handleAxiosError(error)
    }
  }


  /**
   * Send a PUT request to the server
   *
   * @param url The endpoint
   * @param payload PUT payload
   */
  public async put(url: string, payload: any): Promise<any> {
    url = this.baseUrl + url

    // Inject pid and app mode
    const pid = Utils.getPid()
    const appMode = Utils.getAppMode().toLocaleLowerCase()
    payload = {
      ...payload,
      pid: pid,
      app_mode: appMode,
    }

    try {
      const res = await axios.put(url, payload, this.axiosConfig)
      return res.data.payload
    } catch (error) {
      throw this.handleAxiosError(error)
    }
  }


  /**
   * Send a DELETE request to the server
   *
   * @param url The endpoint
   * @param id Selection id
   */
  public async delete(url: string, id: number): Promise<any> {
    url = this.baseUrl + url
    if (id !== null)
      if (url.indexOf('?') !== -1)
        url = url + `&id=${id}`
      else
        `?id=${id}`

    // Inject pid
    const pid = Utils.getPid()
    if (url.indexOf('?') !== -1)
      url = url + `&pid=${pid}`
    else
      `?pid=${pid}`

    // Inject app mode
    const appMode = Utils.getAppMode().toLocaleLowerCase()
    if (url.indexOf('?') !== -1)
      url = url + `&app_mode=${appMode}`
    else
      `?app_mode=${appMode}`

    try {
      const res = await axios.delete(url, this.axiosConfig)
      return res.data.payload
    } catch (error) {
      throw this.handleAxiosError(error)
    }
  }

  /**
   * Extract the backend `{ message }` envelope when present.
   */
  private extractServerMessage(error: AxiosError): string | null {
    const data = error.response?.data
    if (data && typeof data === 'object' && data !== null && 'message' in data) {
      const msg = (data as { message?: unknown }).message
      if (typeof msg === 'string' && msg.trim()) {
        return msg.trim()
      }
    }
    return null
  }

  /**
   * Surface the error to the user and return an Error for the caller to throw.
   *
   * - 401 Unauthorized → session teardown + redirect to login
   * - 403 Forbidden / password-change required → toast only (session kept)
   * - other 4xx/5xx → toast with server message when available
   * - network / no response → connectivity toast
   */
  private handleAxiosError(error: unknown): Error {
    if (!isAxiosError(error)) {
      const message = error instanceof Error ? error.message : 'Unexpected request error.'
      Utils.showErrorMessage(message)
      return error instanceof Error ? error : new Error(message)
    }

    const status = error.response?.status
    const serverMessage = this.extractServerMessage(error)

    if (status === 401) {
      const message = serverMessage || 'Your session has timed out. Please log in again.'
      Utils.showWarningMessage(message)
      setTimeout(() => {
        AuthManager.logout()
        Utils.navigateTo('login', true)
      }, 2000)
      return new Error(message)
    }

    if (status === 403) {
      // Forbidden or forced password change — do not tear down the session.
      const message = serverMessage || 'You do not have permission to perform this action.'
      // While forced to reset password, skip toasts: the status bar (and any
      // other poll) would otherwise spam "Password change required" every 2s.
      if (!store.getters.getMustChangePassword) {
        Utils.showWarningMessage(message)
      }
      return new Error(message)
    }

    if (status && status >= 400) {
      const message = serverMessage || 'Error while processing your request. Please try again.'
      Utils.showErrorMessage(message)
      return new Error(message)
    }

    const message = 'Unable to reach the server. Please check that the application is running and try again.'
    Utils.showErrorMessage(message)
    return new Error(message)
  }
}
